import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useCallback, useState } from 'react'
import toastr from 'toastr'
import { useMutation, useQuery } from '../../../api/graphql'
import cloudinaryTools from '../../../util/cloudinaryTools'
import ResponsiveImage from '../misc/MediaPresets'
import { Hugeicon } from '../../../icons/Hugeicon'

const UserOrganizationsQuery = gql`
  query UserOrganizationsForCalendar {
    currentUser {
      id
      organizationMemberships {
        id
        role
        organization {
          id
          name
          slug
          logoPublicId
          websiteUrl
          hasActiveSubscription
        }
      }
    }
  }
`

const ORGANIZATION_CREATE_MUTATION = gql`
  mutation OrganizationCreate($input: OrganizationCreateInput!) {
    organizationCreate(input: $input) {
      organization {
        id
        name
      }
      errors {
        attribute
        message
      }
    }
  }
`

const ORGANIZATION_TYPE_OPTIONS = [
  { value: 'non_profit', label: 'Non-Profit' },
  { value: 'business', label: 'Business' },
]

type Organization = {
  id: string
  name: string
  slug: string
  logoPublicId: string | null
  websiteUrl: string | null
  hasActiveSubscription: boolean
}

type Props = {
  value: string | null
  onChange: (organizationId: string) => void
  onAutoAdvance?: () => void
}

type FormState = {
  name: string
  organizationType: string
  logoPublicId: string
  streetAddress: string
  city: string
  state: string
  zip: string
  websiteUrl: string
  phone: string
  contactEmail: string
}

const initialFormState: FormState = {
  name: '',
  organizationType: 'business',
  logoPublicId: '',
  streetAddress: '',
  city: '',
  state: '',
  zip: '',
  websiteUrl: '',
  phone: '',
  contactEmail: '',
}

type ViewState = 'list' | 'create'

const OrganizationSelector: React.FC<Props> = ({ value, onChange, onAutoAdvance }) => {
  const { data, loading } = useQuery(UserOrganizationsQuery)
  const autoAdvancedRef = React.useRef(false)
  const [viewState, setViewState] = useState<ViewState>('list')
  const [formState, setFormState] = useState<FormState>(initialFormState)
  const [uploading, setUploading] = useState(false)

  const [createOrganization, { loading: creating }] = useMutation(ORGANIZATION_CREATE_MUTATION, {
    refetchQueries: [{ query: UserOrganizationsQuery }],
  })

  const updateField = (field: keyof FormState, newValue: string) => {
    setFormState(prev => ({ ...prev, [field]: newValue }))
  }

  const formatPhoneNumber = (input: string): string => {
    // Strip all non-digits, limit to 10 digits max (US format)
    const digits = input.replace(/\D/g, '').slice(0, 10)
    // Format progressively as (XXX) XXX-XXXX
    if (digits.length === 0) return ''
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const normalizeWebsiteUrl = (input: string): string => {
    const trimmed = input.trim()
    if (!trimmed) return ''
    // If already has protocol, return as-is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed
    }
    // Add https:// prefix
    return `https://${trimmed}`
  }

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Store raw value while typing - don't auto-format
    updateField('websiteUrl', e.target.value)
  }

  const handleWebsiteBlur = () => {
    // Only normalize on blur to avoid jarring UX while typing
    if (formState.websiteUrl) {
      updateField('websiteUrl', normalizeWebsiteUrl(formState.websiteUrl))
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateField('phone', formatPhoneNumber(e.target.value))
  }

  const resetForm = () => {
    setFormState(initialFormState)
  }

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toastr.error('Please select a valid image file.')
      return
    }

    setUploading(true)

    const uploadTimeout = setTimeout(() => {
      setUploading(false)
      toastr.error('Logo upload timed out. Please try again.')
    }, 15000)

    try {
      await cloudinaryTools.upload(file, { folder: 'logo' }, (error, response) => {
        clearTimeout(uploadTimeout)
        setUploading(false)

        if (error) {
          toastr.error(error)
        } else {
          updateField('logoPublicId', (response as { public_id: string }).public_id)
        }
      })
    } catch {
      clearTimeout(uploadTimeout)
      setUploading(false)
      toastr.error('Logo upload failed. Please try again.')
    }
  }, [])

  const handleRemoveLogo = useCallback(() => {
    updateField('logoPublicId', '')
  }, [])

  const memberships = data?.currentUser?.organizationMemberships || []
  const ownerMemberships = memberships.filter((m: { role: string }) => m.role === 'owner')
  const organizations: Organization[] = ownerMemberships.map((m: { organization: Organization }) => m.organization)

  // Auto-select and advance if user has exactly one eligible org
  const eligibleOrgs = organizations.filter(org => !org.hasActiveSubscription)
  React.useEffect(() => {
    if (!loading && eligibleOrgs.length === 1 && !autoAdvancedRef.current && onAutoAdvance) {
      autoAdvancedRef.current = true
      onChange(eligibleOrgs[0].id)
      onAutoAdvance()
    }
  }, [loading, eligibleOrgs.length]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Container>
        <Title>Who is this calendar for?</Title>
        <LoadingState>Loading your organizations...</LoadingState>
      </Container>
    )
  }

  const handleOrgSelected = (org: Organization) => {
    onChange(org.id)
  }

  const isValidEmail = (email: string): boolean => {
    if (!email) return true // Empty is valid (field is optional)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const isValidUrl = (url: string): boolean => {
    if (!url) return true // Empty is valid (field is optional)
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const handleCreate = async () => {
    if (!formState.name.trim()) {
      toastr.error('Organization name is required')
      return
    }

    if (formState.contactEmail && !isValidEmail(formState.contactEmail)) {
      toastr.error('Please enter a valid email address')
      return
    }

    if (formState.websiteUrl && !isValidUrl(formState.websiteUrl)) {
      toastr.error('Please enter a valid website URL')
      return
    }

    try {
      const result = await createOrganization({
        variables: {
          input: {
            name: formState.name.trim(),
            organizationType: formState.organizationType || null,
            logoPublicId: formState.logoPublicId || null,
            streetAddress: formState.streetAddress || null,
            city: formState.city || null,
            state: formState.state || null,
            zip: formState.zip || null,
            websiteUrl: formState.websiteUrl || null,
            phone: formState.phone || null,
            contactEmail: formState.contactEmail || null,
          },
        },
      })

      const response = result?.data?.organizationCreate
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      const newOrg = response?.organization
      if (newOrg) {
        toastr.success('Organization created!')
        onChange(newOrg.id)
        resetForm()
        setViewState('list')
        if (onAutoAdvance) {
          onAutoAdvance()
        }
      }
    } catch {
      toastr.error('Failed to create organization. Please try again.')
    }
  }

  const renderCreateForm = (showBack: boolean) => (
    <CreateFormContainer>
      <CreateFormTitle>Create Your Organization</CreateFormTitle>
      <CreateFormSubtitle>Tell us about your business or team</CreateFormSubtitle>

      <FormFields>
        <FormRow>
          <FormField style={{ flex: 1 }}>
            <FormLabel>Organization Name *</FormLabel>
            <FormInput
              type="text"
              value={formState.name}
              onChange={e => updateField('name', e.target.value)}
              placeholder="Your business or team name"
              autoFocus
            />
          </FormField>

          <FormField style={{ width: '160px' }}>
            <FormLabel>Type</FormLabel>
            <FormSelect value={formState.organizationType} onChange={e => updateField('organizationType', e.target.value)}>
              {ORGANIZATION_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormSelect>
          </FormField>
        </FormRow>

        <LogoUploadField>
          <FormLabel>Organization Logo</FormLabel>
          <LogoUploadContent>
            <LogoPreview>
              {formState.logoPublicId ? (
                <ResponsiveImage publicId={formState.logoPublicId} width="80" height="80" />
              ) : (
                <PlaceholderLogo>
                  <BuildingIcon />
                </PlaceholderLogo>
              )}
            </LogoPreview>
            <LogoActions>
              <UploadLabel htmlFor="org-logo-upload" isUploading={uploading}>
                <CameraIcon />
                {uploading ? 'UPLOADING...' : 'UPLOAD LOGO'}
                <input id="org-logo-upload" type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
              </UploadLabel>
              {formState.logoPublicId && (
                <RemoveLogoButton type="button" onClick={handleRemoveLogo}>
                  Remove
                </RemoveLogoButton>
              )}
            </LogoActions>
          </LogoUploadContent>
        </LogoUploadField>

        <FormField>
          <FormLabelWithIcon>
            <LocationIcon />
            Address
          </FormLabelWithIcon>
          <FormInput
            type="text"
            value={formState.streetAddress}
            onChange={e => updateField('streetAddress', e.target.value)}
            placeholder="Street Address"
          />
          <AddressRow>
            <FormInputSmall
              type="text"
              value={formState.city}
              onChange={e => updateField('city', e.target.value)}
              placeholder="City"
            />
            <FormInputSmall
              type="text"
              value={formState.state}
              onChange={e => updateField('state', e.target.value)}
              placeholder="State"
              style={{ maxWidth: '100px' }}
            />
            <FormInputSmall
              type="text"
              value={formState.zip}
              onChange={e => updateField('zip', e.target.value)}
              placeholder="Zip"
              style={{ maxWidth: '100px' }}
            />
          </AddressRow>
        </FormField>

        <TwoColumnRow>
          <FormField>
            <FormLabelWithIcon>
              <GlobeIcon />
              Website
            </FormLabelWithIcon>
            <FormInput
              type="url"
              value={formState.websiteUrl}
              onChange={handleWebsiteChange}
              onBlur={handleWebsiteBlur}
              placeholder="https://example.com"
            />
          </FormField>

          <FormField>
            <FormLabelWithIcon>
              <PhoneIcon />
              Phone
            </FormLabelWithIcon>
            <FormInput
              type="tel"
              value={formState.phone}
              onChange={handlePhoneChange}
              placeholder="(555) 555-5555"
            />
          </FormField>
        </TwoColumnRow>

        <FormField>
          <FormLabelWithIcon>
            <EmailIcon />
            Contact Email
          </FormLabelWithIcon>
          <FormInput
            type="email"
            value={formState.contactEmail}
            onChange={e => updateField('contactEmail', e.target.value)}
            placeholder="contact@example.com"
          />
        </FormField>
      </FormFields>

      <FormActions>
        {showBack && (
          <BackButton
            type="button"
            onClick={() => {
              setViewState('list')
              resetForm()
            }}
          >
            Back
          </BackButton>
        )}
        <CreateButton type="button" disabled={creating || !formState.name.trim()} onClick={handleCreate}>
          {creating ? 'Creating...' : 'Create & Continue'}
        </CreateButton>
      </FormActions>
    </CreateFormContainer>
  )

  // No orgs: show create form directly
  if (organizations.length === 0 && viewState === 'list') {
    return (
      <Container>
        <Title>Who is this calendar for?</Title>
        {renderCreateForm(false)}
      </Container>
    )
  }

  // Create form
  if (viewState === 'create') {
    return (
      <Container>
        <Title>Who is this calendar for?</Title>
        {renderCreateForm(true)}
      </Container>
    )
  }

  // Default: show org list
  return (
    <Container>
      <Title>Who is this calendar for?</Title>
      <Description>Select your business or team, or create a new one</Description>

      <OrganizationList>
        {organizations.map(org => (
          <OrganizationCard
            key={org.id}
            selected={value === org.id}
            disabled={org.hasActiveSubscription}
            onClick={() => !org.hasActiveSubscription && handleOrgSelected(org)}
          >
            <OrgLogo>
              {org.logoPublicId ? (
                <img
                  src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/w_80,h_80,c_fill/${org.logoPublicId}`}
                  alt={org.name}
                />
              ) : (
                <DefaultOrgIcon />
              )}
            </OrgLogo>
            <OrgInfo>
              <OrgName>{org.name}</OrgName>
              {org.hasActiveSubscription && <ActiveBadge>Already has calendar</ActiveBadge>}
            </OrgInfo>
            {value === org.id && <SelectedIndicator />}
          </OrganizationCard>
        ))}

        <CreateNewCard onClick={() => setViewState('create')}>
          <CreateNewIcon>
            <PlusIcon />
          </CreateNewIcon>
          <OrgInfo>
            <OrgName>Create new</OrgName>
          </OrgInfo>
        </CreateNewCard>
      </OrganizationList>
    </Container>
  )
}

export default OrganizationSelector

const BuildingIcon: React.FC = () => (
  <Hugeicon name="bank" size={24} />
)

const DefaultOrgIcon: React.FC = () => (
  <Hugeicon name="building" size={28} />
)

const PlusIcon: React.FC = () => (
  <Hugeicon name="plus" size={24} />
)

const LocationIcon: React.FC = () => (
  <Hugeicon name="location" size={16} />
)

const GlobeIcon: React.FC = () => (
  <Hugeicon name="globe" size={16} />
)

const PhoneIcon: React.FC = () => (
  <Hugeicon name="call" size={16} />
)

const EmailIcon: React.FC = () => (
  <Hugeicon name="mail" size={16} />
)

const CameraIcon: React.FC = () => (
  <Hugeicon name="camera" size={14} />
)

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
`

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 8px;
  text-align: center;
`

const Description = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px;
  text-align: center;
`

const LoadingState = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 48px;
`

const OrganizationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const OrganizationCard = styled.div<{ selected: boolean; disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  background: ${props => (props.selected ? '#f0f7ff' : '#ffffff')};
  border: 2px solid ${props => (props.selected ? '#3b82f6' : '#e5e7eb')};
  border-radius: 12px;
  padding: 16px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => (props.disabled ? '#e5e7eb' : props.selected ? '#3b82f6' : '#9ca3af')};
  }
`

const CreateNewCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9ca3af;
    background: #f3f4f6;
  }
`

const CreateNewIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
`

const OrgLogo = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #9ca3af;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const OrgInfo = styled.div`
  flex: 1;
`

const OrgName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
`

const ActiveBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  color: #22c55e;
  margin-top: 4px;
`

const SelectedIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: white;
  }
`

const CreateFormContainer = styled.div`
  text-align: left;
  padding: 32px;
  background: #f9fafb;
  border-radius: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`

const CreateFormTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 8px;
  text-align: center;
`

const CreateFormSubtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 28px;
  text-align: center;
`

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FormLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const FormLabelWithIcon = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const FormInput = styled.input`
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #102638;
  transition: border-color 0.15s ease-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  &::placeholder {
    color: #9ca3af;
  }
`

const FormInputSmall = styled(FormInput)`
  flex: 1;
  min-width: 0;
`

const FormSelect = styled.select`
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #102638;
  cursor: pointer;
  transition: border-color 0.15s ease-out;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`

const AddressRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const LogoUploadField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LogoUploadContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const LogoPreview = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const PlaceholderLogo = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
  color: white;
`

const LogoActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const UploadLabel = styled.label<{ isUploading: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #1e3a5f;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: ${props => (props.isUploading ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.isUploading ? 0.7 : 1)};
  transition: background-color 0.15s ease-out;

  &:hover {
    background: ${props => (props.isUploading ? '#1e3a5f' : '#2d4a6f')};
  }

  input {
    display: none;
  }
`

const RemoveLogoButton = styled.button`
  background: transparent;
  color: #dc2626;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

const FormActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`

const BackButton = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-out;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`

const CreateButton = styled.button`
  background: #1e3a5f;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #2d4a6f;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

